import { useMemo, useState } from 'react'

import {
  F0Box,
  F0DataChart,
  F0Heading,
  F0Icon,
  F0TagStatus,
  F0Text,
  OneEmptyState,
} from '@factorialco/f0-react'
import { OneDateNavigator, Spinner } from '@factorialco/f0-react/dist/experimental'
import type { DatePickerValue, DatePreset } from '@factorialco/f0-react/dist/experimental'
import { ChartLine } from '@factorialco/f0-react/icons/app'
import { addDays, format, parseISO, startOfDay, subDays } from 'date-fns'

import {
  AiCreditsEnumsBillableBucketEnum,
  AiCreditsEnumsUsageGranularityEnum,
  AiCreditsUsageReportDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

type PresetId = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'last_12_months'

interface PresetConfig {
  id: PresetId
  label: string
  days: number
}

const buildPresets = (): PresetConfig[] => [
  {
    id: 'last_7_days',
    label: i18n.t('ai.settings.usage_tab.presets.last_7_days'),
    days: 7,
  },
  {
    id: 'last_30_days',
    label: i18n.t('ai.settings.usage_tab.presets.last_30_days'),
    days: 30,
  },
  {
    id: 'last_90_days',
    label: i18n.t('ai.settings.usage_tab.presets.last_90_days'),
    days: 90,
  },
  {
    id: 'last_12_months',
    label: i18n.t('ai.settings.usage_tab.presets.last_12_months'),
    days: 365,
  },
]

const DEFAULT_PRESET_ID: PresetId = 'last_7_days'

const FALLBACK_PRESET: PresetConfig = {
  id: 'last_7_days',
  label: '',
  days: 7,
}

const rangeFor = (preset: PresetConfig): { from: Date; to: Date } => {
  const today = startOfDay(new Date())
  return { from: subDays(today, preset.days - 1), to: today }
}

export const UsagePage = () => {
  const { company } = useCurrentSession()

  useEffect(() => {
    track(trackingEvents.one.visitOneUsagePage)
  }, [])

  const presets = useMemo(() => buildPresets(), [])

  const defaultPreset = useMemo(
    () => presets.find((p) => p.id === DEFAULT_PRESET_ID) ?? presets[0] ?? FALLBACK_PRESET,
    [presets]
  )

  const [selection, setSelection] = useState(() => {
    const range = rangeFor(defaultPreset)
    return { from: range.from, to: range.to }
  })

  const { from, to } = selection

  const { startDate, endDate } = useMemo(() => {
    return {
      startDate: toIsoDate(from),
      endDate: toIsoDate(to),
    }
  }, [from, to])

  const datePickerPresets: DatePreset[] = useMemo(
    () =>
      presets.map((p) => {
        const range = rangeFor(p)
        return {
          label: p.label,
          granularity: 'range',
          value: { from: range.from, to: range.to },
        }
      }),
    [presets]
  )

  const { data, previousData, networkActivity, loading } = useQuery(AiCreditsUsageReportDocument, {
    variables: {
      companyId: company.id,
      startDate,
      endDate,
    },
  })

  // Fall back to the last successful payload while a refetch is in flight so
  // the chart and chrome don't unmount when the date range changes.
  const effectiveData = data ?? previousData
  const report = effectiveData?.aiCredits.creditUsageReportsConnection.nodes[0]
  const status = effectiveData?.aiCredits.creditStatusesConnection.nodes[0]
  const isInitialLoad = loading && !effectiveData

  const poolTotal = status?.monthlyAllocation ?? 0
  const poolRemaining = status?.remainingCredits ?? 0
  const poolUsed = Math.max(poolTotal - poolRemaining, 0)
  const poolRenewsAt = status?.renewsAt
    ? i18nDateToString(new Date(status.renewsAt), 'ui_dm_long')
    : null

  const handleSelect = (next: DatePickerValue | undefined) => {
    if (!next?.value) return
    const raw = next.value
    const nextFrom = raw instanceof Date ? raw : raw.from
    const nextTo = raw instanceof Date ? raw : (raw.to ?? raw.from)

    setSelection({ from: nextFrom, to: nextTo })
  }

  if (isInitialLoad) {
    return <Loading />
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="2xl" width="full">
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Box alignItems="center" display="flex" gap="sm">
          <F0Icon icon={ChartLine} size="lg" />
          <F0Heading content={i18n.t('ai.settings.usage_tab.title')} variant="heading-large" />
        </F0Box>
        <F0Text content={i18n.t('ai.settings.usage_tab.description')} variant="description" />
      </F0Box>

      <F0Box display="flex" alignItems="center" justifyContent="between" gap="md" width="full">
        <F0Box display="flex" alignItems="center" gap="xl">
          <F0Heading
            content={i18n.t('ai.settings.usage_tab.pool.usage', {
              used: poolUsed,
              total: poolTotal,
            })}
            variant="heading"
          />
          {poolRenewsAt ? (
            <F0TagStatus
              variant="neutral"
              text={i18n.t('ai.settings.usage_tab.pool.resets_at', { date: poolRenewsAt })}
            />
          ) : null}
        </F0Box>
        <F0Box display="flex" alignItems="center" gap="sm">
          <OneDateNavigator
            value={{
              value: { from, to },
              granularity: 'range',
            }}
            granularities={['range']}
            presets={datePickerPresets}
            maxDate={new Date()}
            hideNavigation
            hideGoToCurrent
            onSelect={handleSelect}
          />
          {networkActivity ? <Spinner size="small" /> : null}
        </F0Box>
      </F0Box>

      <ChartCard report={report} />
    </F0Box>
  )
}

interface ChartCardProps {
  report:
    | {
        granularity: AiCreditsEnumsUsageGranularityEnum
        series: ReadonlyArray<{
          billableBucket: AiCreditsEnumsBillableBucketEnum
          displayName: string
          points: ReadonlyArray<{
            periodStart: string
            transactionCount: number
            creditsConsumed: number
          }>
        }>
      }
    | undefined
}

const formatPeriodLabel = (
  periodStart: string,
  granularity: AiCreditsEnumsUsageGranularityEnum
): string => {
  const date = parseISO(periodStart)
  switch (granularity) {
    case 'day':
      return format(date, 'd MMM')
    case 'week': {
      const end = addDays(date, 6)
      const startFmt = date.getMonth() === end.getMonth() ? 'd' : 'd MMM'
      return `${format(date, startFmt)}–${format(end, 'd MMM')}`
    }
    case 'month':
      return format(date, 'MMM yyyy')
  }
}

const ChartCard = ({ report }: ChartCardProps) => {
  const { categories, series } = useMemo(() => buildChartData(report), [report])
  const isEmpty = series.length === 0

  return (
    <F0Box border="default" borderRadius="lg" padding="lg">
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Heading content={i18n.t('ai.settings.usage_tab.chart.title')} variant="heading" />
        {isEmpty ? (
          <F0Box paddingY="4xl">
            <OneEmptyState
              emoji="📊"
              title={i18n.t('ai.settings.usage_tab.chart.empty_title')}
              description={i18n.t('ai.settings.usage_tab.chart.empty_description')}
            />
          </F0Box>
        ) : (
          <F0Box width="full" height="80">
            <F0DataChart
              type="line"
              categories={categories}
              series={series}
              showArea={false}
              showDots
              lineType="smooth"
              echartsOptions={{
                tooltip: {
                  trigger: 'axis',
                  confine: true,
                  order: 'valueDesc',
                  formatter: (
                    params:
                      | {
                          value?: number | string
                          axisValueLabel?: string
                          name?: string
                          marker?: string
                          seriesName?: string
                        }
                      | Array<{
                          value?: number | string
                          axisValueLabel?: string
                          name?: string
                          marker?: string
                          seriesName?: string
                        }>
                  ) => {
                    if (!Array.isArray(params)) return ''
                    const items = params.filter((p) => Number(p.value) > 0)
                    const first = items[0]
                    if (!first) return ''
                    const header = `<div style="margin-bottom: 4px; font-weight: 500">${String(first.axisValueLabel ?? first.name ?? '')}</div>`
                    const rows = items
                      .map(
                        (p) =>
                          `<div>${String(p.marker ?? '')} ${String(p.seriesName ?? '')} <strong>${String(p.value)}</strong></div>`
                      )
                      .join('')
                    return `${header}${rows}`
                  },
                },
              }}
            />
          </F0Box>
        )}
      </F0Box>
    </F0Box>
  )
}

interface BuildChartData {
  categories: string[]
  series: Array<{ name: string; data: number[] }>
}

const buildChartData = (report: ChartCardProps['report']): BuildChartData => {
  if (!report || report.series.length === 0) {
    return { categories: [], series: [] }
  }

  // All series share the same periods (backend pads zeros), so derive
  // categories from the first non-empty series.
  const reference = report.series.find((s) => s.points.length > 0) ?? report.series[0]
  if (!reference) {
    return { categories: [], series: [] }
  }

  const categories = reference.points.map((p) =>
    formatPeriodLabel(p.periodStart, report.granularity)
  )

  const series = report.series
    .map((s) => {
      const total = s.points.reduce((acc, p) => acc + p.creditsConsumed, 0)
      const data = s.points.map((p) => p.creditsConsumed)
      return { name: s.displayName, data, total }
    })
    .filter((s) => s.total > 0)
    .sort((a, b) => b.total - a.total)
    .map(({ name, data }) => ({ name, data }))

  return { categories, series }
}

const toIsoDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
